/**
 * Simple Authentication service for handling JWT token refresh and authentication state
 */
import store from '../store';

// Flag to prevent multiple refresh requests
let isRefreshing = false;

// Flag to track if user is being logged out 
let isLoggingOut = false;

/**
 * Set the logging out flag to prevent token refreshes during logout
 */
export function setLoggingOut(value) {
	isLoggingOut = value;
}

/**
 * Refresh the access token using the refresh token
 * @returns {Promise<boolean>} True if refresh was successful
 */
export async function refreshToken() {
	// Check if we're in the process of logging out using memory flag
	if (isLoggingOut) {
		console.log('Already logging out, skipping token refresh');
		return false;
	}

	// Prevent multiple refresh requests at the same time
	if (isRefreshing) {
		return new Promise(resolve => {
			const checkRefreshComplete = setInterval(() => {
				if (!isRefreshing) {
					clearInterval(checkRefreshComplete);
					// Return the current auth state rather than assuming success
					resolve(!!store.state.user);
				}
			}, 100);
		});
	}

	isRefreshing = true;

	try {
		// Call the refresh endpoint
		const response = await fetch('/api/auth/refresh', {
			method: 'POST',
			credentials: 'include', // Important for cookies
			headers: {
				'Content-Type': 'application/json',
				'Cache-Control': 'no-cache'
			}
		});
		
		if (!response.ok) {
			console.log(`Token refresh failed with status: ${response.status}`);
			
			// Don't call logout here, let the interceptor handle it
			throw new Error(`Token refresh failed: ${response.status}`);
		}

		const data = await response.json();
		
		if (data.success) {
			// Token is automatically set in the cookie by the server
			try {
				// Fetch the user info with the new token
				await fetchUserInfo();
				return true;
			} catch (userInfoError) {
				console.error('Error fetching user info after token refresh:', userInfoError);
				return false;
			}
		} else {
			throw new Error(data.message || 'Token refresh failed');
		}
	} catch (error) {
		// Just return false, let the interceptor handle logout
		console.error('Error in refreshToken():', error.message);
		return false;
	} finally {
		isRefreshing = false;
	}
}

/**
 * Fetch current user information
 * @returns {Promise<Object>} User information
 */
export async function fetchUserInfo() {
	try {
		const response = await fetch('/api/user/me', {
			credentials: 'include' // Important for cookies
		});

		if (!response.ok) {
			throw new Error('Failed to fetch user info');
		}

		const user = await response.json();
		await store.dispatch('setUser', user);
		return user;
	} catch (error) {
		console.error('Error fetching user info:', error);
		throw error;
	}
}

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>} True if user is authenticated
 */
export async function checkAuth() {
	try {
		await fetchUserInfo();
		return true;
	} catch (error) {
		return false;
	}
}

/**
 * Logout user
 * @returns {Promise<void>}
 */
export async function logout() {
	await store.dispatch('logout');
}
